import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { aenderungText, ZUSTAND } from '../lib/cmdb'
import { formatDateTime } from '../lib/format'
import type { CmdbAenderung, DienstFolge } from '../lib/types'

/**
 * Welche Dienste an einem Gerät hängen (Stufe 5.4). Gerechnet wird „nur
 * dieses Gerät fällt aus“; ohne Dienste bleibt die Karte weg.
 */
export function GeraetDiensteKarte({ deviceId }: { deviceId: string }) {
  const folgen = useQuery({
    queryKey: ['device-dienste', deviceId],
    queryFn: ({ signal }) => apiGet<DienstFolge[]>(`/devices/${deviceId}/dienste`, signal),
  })
  // Eine Antwort ohne Liste (etwa von einer älteren Zentrale) zeigt nichts.
  const liste = Array.isArray(folgen.data) ? folgen.data : []
  if (liste.length === 0) return null
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Dienste</h2>
        <span className="muted">bei Ausfall dieses Geräts</span>
      </div>
      <table className="data">
        <tbody>
          {liste.map((f) => (
            <tr key={f.dienst_id}>
              <td>{f.dienst}</td>
              <td className="muted">{f.direkt ? 'direkt' : 'über einen anderen Dienst'}</td>
              <td style={{ textAlign: 'right' }}>
                <span className={ZUSTAND[f.bei_ausfall].klasse}>{ZUSTAND[f.bei_ausfall].text}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

/** Die Geschichte der Stammdaten eines Geräts. */
export function GeraetAenderungenKarte({ deviceId }: { deviceId: string }) {
  const liste = useQuery({
    queryKey: ['cmdb', deviceId],
    queryFn: ({ signal }) => apiGet<CmdbAenderung[]>(`/cmdb/aenderungen?objekt=${deviceId}&anzahl=50`, signal),
  })
  const zeilen = Array.isArray(liste.data) ? liste.data : []
  if (zeilen.length === 0) return null
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Änderungen der Stammdaten</h2>
      </div>
      <AenderungenTabelle zeilen={zeilen} mitObjekt={false} />
    </section>
  )
}

export function AenderungenTabelle({ zeilen, mitObjekt }: { zeilen: CmdbAenderung[]; mitObjekt: boolean }) {
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Zeit</th>
          {mitObjekt ? <th>Objekt</th> : null}
          <th>Änderung</th>
          <th>Von</th>
        </tr>
      </thead>
      <tbody>
        {zeilen.map((h) => (
          <tr key={h.id}>
            <td className="muted">{formatDateTime(h.zeit)}</td>
            {mitObjekt ? (
              <td>
                {h.objekt === 'dienst' ? 'Dienst ' : ''}
                {h.name}
                <div className="muted">{h.kunde}</div>
              </td>
            ) : null}
            <td>{aenderungText(h)}</td>
            <td>{h.von || <span className="muted">erkannt</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
